import { useEffect, useRef, useState } from 'react';
import type { Edl } from '@repo/editor-core';
import { DEFAULT_BRANDING } from './branding';
import { DeadZoneGuide, ShortsChrome } from './ShortsChrome';
import type { Engine } from './useEngine';

const OUTPUT_ASPECT = 9 / 16; // width / height of the exported short

/**
 * 폰 테두리 두께(px). 화면을 «기기 안의 것»으로 보이게 하는 최소한이고,
 * 고정값이라 프리뷰 폭 계산이 순환하지 않는다(폭은 무대 높이에서 나온다).
 */
const BEZEL = 8;

export function PreviewPane({
  engine,
  edl,
  playing,
  onToggle,
  showGuide = true,
  channelName,
}: {
  engine: Engine;
  edl: Edl;
  /** Playback state — drives the paused ▶ overlay. */
  playing?: boolean;
  /** Click-to-toggle play/pause (replaces the removed transport bar). */
  onToggle?: () => void;
  /**
   * 쇼츠 앱 UI 가 덮는 자리를 보여줄지. 끄면 결과물만 남는다(폰 테두리는 유지 —
   * 껐다 켤 때 프리뷰 크기가 흔들리지 않아야 비교가 된다).
   */
  showGuide?: boolean;
  /**
   * 앱 UI 재현에 띄울 채널 이름. **컴포지터가 캔버스에 그리는 이름과 같아야 한다** —
   * 다르면 한 화면에 채널 이름이 둘로 보인다(실측: 기본값을 쓰다가 캔버스에는
   * 「Hakko Kim」, 앱 UI 에는 「yoy.live」가 동시에 찍혔다).
   */
  channelName?: string;
}) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);

  // The 9:16 video's width is driven by the available stage HEIGHT — measure it
  // and size the whole pane to that width so the pane hugs the video (no wasted
  // horizontal band) and stays flush-left, leaving the rest for the controls.
  const [canvasWidth, setCanvasWidth] = useState(0);
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      // 테두리도 세로 공간을 먹는다 — 빼고 나서 9:16 을 맞춰야 폰이 무대를 넘지 않는다.
      const inner = el.clientHeight - BEZEL * 2;
      setCanvasWidth(inner > 0 ? Math.round(inner * OUTPUT_ASPECT) : 0);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Mount the compositor's canvas once. 캔버스 상자가 이미 정확히 9:16 이라
  // 100%×100% 로 채우면 되고, 그래야 오버레이의 % 좌표와 픽셀이 일치한다.
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const canvas = engine.compositor.canvas;
    canvas.style.height = '100%';
    canvas.style.width = '100%';
    canvas.style.display = 'block';
    mount.appendChild(canvas);
    return () => {
      if (canvas.parentElement === mount) mount.removeChild(canvas);
    };
  }, [engine]);

  // Feed EDL changes into the player (re-renders current frame) + warm up the
  // clip decoders so playback doesn't stall on a black frame.
  useEffect(() => {
    engine.player.setEdl(edl);
    const assetIds: string[] = [];
    for (const track of edl.tracks) {
      for (const item of track.items) {
        if (item.type === 'clip' && (track.kind === 'video' || track.kind === 'broll')) {
          assetIds.push(item.source.assetId);
        } else if (item.type === 'gif') {
          assetIds.push(item.src); // warm GIF decoders too (natural length for looping)
        }
      }
    }
    engine.pool.preload(assetIds);
  }, [engine, edl]);

  const paneWidth = canvasWidth ? canvasWidth + BEZEL * 2 : undefined;

  return (
    <div className="flex h-full flex-col" style={{ width: paneWidth }}>
      {/* Stage height drives the pane width (above), so the phone fills exactly
          with no black bars. Clicking toggles play/pause (there is no transport
          bar); a ▶ overlay marks the paused state. */}
      <div
        ref={stageRef}
        onClick={onToggle}
        className={`relative flex min-h-0 flex-1 items-center justify-center ${
          onToggle ? 'cursor-pointer' : ''
        }`}
      >
        <div
          className="relative"
          style={{
            padding: BEZEL,
            background: '#111114',
            borderRadius: canvasWidth * 0.095 + BEZEL,
            boxShadow: '0 0 0 1px #2b2b31, 0 18px 40px -18px rgba(0,0,0,.9)',
          }}
        >
          <div
            className="relative overflow-hidden bg-black"
            style={{
              width: canvasWidth || undefined,
              aspectRatio: '9 / 16',
              borderRadius: canvasWidth * 0.095,
            }}
          >
            <div ref={mountRef} className="absolute inset-0" />
            {showGuide && canvasWidth > 0 && (
              <>
                <DeadZoneGuide />
                <ShortsChrome
                  w={canvasWidth}
                  channelName={channelName || DEFAULT_BRANDING.channelName}
                />
              </>
            )}
            {onToggle && playing === false && (
              <div
                className="pointer-events-none absolute inset-0 flex items-center justify-center"
                style={{ zIndex: 6 }}
              >
                <div className="grid h-16 w-16 place-items-center rounded-full bg-black/45 pl-1 text-2xl text-white">
                  ▶
                </div>
              </div>
            )}
          </div>
          {/* 기기 상단 컷아웃. 이것도 «덮이는 자리»라 가이드와 함께 여닫는다 —
              끈 상태에서 혼자 남으면 결과물 위에 검은 알약이 얹힌 꼴이 된다. */}
          {showGuide && canvasWidth > 0 && (
            <div
              className="absolute rounded-full bg-black"
              style={{
                zIndex: 7,
                top: BEZEL + canvasWidth * 0.026,
                left: '50%',
                transform: 'translateX(-50%)',
                width: canvasWidth * 0.29,
                height: canvasWidth * 0.072,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
